import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { getTaxDeadlines } from '../../data/taxes';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { AlertTriangle, Clock, Shield, Info } from 'lucide-react';
import TaxProvisioningPanel from './TaxProvisioningPanel';

/**
 * Fiscalité — Vue d'ensemble. Trois blocs : pending actions, calendrier
 * fiscal (12 mois) et alertes. Pulled out of Fiscalite.jsx on 28/05/2026
 * to keep each tab under 200 LOC and testable in isolation.
 */
export default function OverviewTab() {
  const {
    tvaPendingDeclaration, tvaCollectedTotal, tvaDeductibleTotal, payTvaDeclaration,
    tvaInstallmentSchedule, tvaInstallmentUsedThisYear, requestTvaInstallment,
    isPendingInstallment, payIsInstallment, takeIsBankCredit, annualProfit,
    cfeDue, payCfe, essieuDue, payTaxeEssieu,
    taxRegime, month, year, treasury, taxAlerts,
  } = useGameStore();

  const netTva = tvaCollectedTotal - tvaDeductibleTotal;
  // 12/02/2026 — Lissage début d'année (cf. projectAnnualProfit).
  const { annualIS: estimatedIS } = projectAnnualProfit({ annualProfit, month });
  const pendingCount = (tvaPendingDeclaration ? 1 : 0) + (isPendingInstallment ? 1 : 0) +
    (cfeDue > 0 ? 1 : 0) + (essieuDue > 0 ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* 30/05/2026 — Tax provisioning + Réserve fiscale (Midal perk) */}
      <TaxProvisioningPanel />

      {/* Pending actions */}
      {pendingCount > 0 && (
        <div className="bg-warn/10 border border-warn/40 rounded-lg p-4 space-y-2" data-testid="tax-pending-actions">
          <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Actions fiscales en attente
          </h3>
          {tvaPendingDeclaration && (
            <div className="flex items-center justify-between bg-ink-800 rounded-lg p-3 border border-amber-100" data-testid="tva-pending-block">
              <div>
                <p className="text-sm font-medium text-slate-200">Déclaration TVA</p>
                <p className="text-xs text-slate-500">Net à payer : {formatMoney(Math.max(0, netTva))}</p>
                {netTva > 0 && treasury < netTva && !tvaInstallmentUsedThisYear && (
                  <p className="text-[10px] text-amber-300 mt-1" data-testid="tva-installment-hint">
                    💡 Trésorerie insuffisante — vous pouvez demander un échéancier DGFiP (6 mensualités + 0,4 %/mois).
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={payTvaDeclaration}
                  className="text-xs bg-accent text-white px-3 py-1.5 rounded-md font-medium hover:bg-accent-hover"
                  data-testid="pay-tva-btn"
                >
                  Déclarer & Payer
                </button>
                {netTva > 0 && treasury < netTva && !tvaInstallmentUsedThisYear && (
                  <button
                    onClick={() => {
                      const r = requestTvaInstallment();
                      if (!r.ok) window.alert(r.reason);
                    }}
                    className="text-xs bg-amber-600/30 hover:bg-amber-600/50 border border-amber-500/50 text-amber-100 px-3 py-1.5 rounded-md font-medium"
                    data-testid="request-tva-installment-btn"
                  >
                    Demander échéancier
                  </button>
                )}
              </div>
            </div>
          )}
          {(tvaInstallmentSchedule || []).length > 0 && (
            <div className="bg-ink-800 rounded-lg p-3 border border-amber-100" data-testid="tva-schedule-block">
              <p className="text-sm font-medium text-slate-200 mb-1.5">Échéancier TVA en cours</p>
              <ul className="text-[11px] text-slate-400 space-y-0.5">
                {tvaInstallmentSchedule.map(s => (
                  <li key={`tva-inst-${s.index}`} className="flex justify-between" data-testid={`tva-inst-row-${s.index}`}>
                    <span>{s.paid ? '✅' : '⏳'} M{s.dueMonth.toString().padStart(2, '0')}/{s.dueYear}</span>
                    <span>{formatMoney(s.amount + s.surcharge)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {isPendingInstallment && (
            <div className="flex items-center justify-between bg-ink-800 rounded-lg p-3 border border-amber-100" data-testid="is-pending-block">
              <div>
                <p className="text-sm font-medium text-slate-200">Acompte IS T{Math.ceil(month / 3)}</p>
                <p className="text-xs text-slate-500">Estimé : {formatMoney(Math.round(estimatedIS / 4))}</p>
                {treasury < Math.round(estimatedIS / 4) && (annualProfit || 0) > 0 && (
                  <p className="text-[10px] text-emerald-300 mt-1" data-testid="is-credit-hint">
                    💳 Entreprise rentable mais tréso insuffisante — votre banque peut financer (4,5 % / 6 mois).
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={payIsInstallment}
                  className="text-xs bg-accent text-white px-3 py-1.5 rounded-md font-medium hover:bg-accent-hover"
                  data-testid="pay-is-btn"
                >
                  Payer l&apos;acompte
                </button>
                {treasury < Math.round(estimatedIS / 4) && (annualProfit || 0) > 0 && (
                  <button
                    onClick={() => {
                      const r = takeIsBankCredit();
                      if (!r.ok) window.alert(r.reason);
                    }}
                    className="text-xs bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-500/50 text-emerald-100 px-3 py-1.5 rounded-md font-medium"
                    data-testid="take-is-credit-btn"
                  >
                    Crédit bancaire fiscal
                  </button>
                )}
              </div>
            </div>
          )}
          {cfeDue > 0 && (
            <div className="flex items-center justify-between bg-ink-800 rounded-lg p-3 border border-amber-100">
              <div>
                <p className="text-sm font-medium text-slate-200">CFE annuelle</p>
                <p className="text-xs text-slate-500">Montant : {formatMoney(cfeDue)}</p>
              </div>
              <button onClick={payCfe} className="text-xs bg-accent text-white px-3 py-1.5 rounded-md font-medium hover:bg-accent-hover" data-testid="pay-cfe-btn">
                Payer
              </button>
            </div>
          )}
          {essieuDue > 0 && (
            <div className="flex items-center justify-between bg-ink-800 rounded-lg p-3 border border-amber-100">
              <div>
                <p className="text-sm font-medium text-slate-200">Taxe à l&apos;essieu</p>
                <p className="text-xs text-slate-500">Montant : {formatMoney(essieuDue)}</p>
              </div>
              <button onClick={payTaxeEssieu} className="text-xs bg-accent text-white px-3 py-1.5 rounded-md font-medium hover:bg-accent-hover" data-testid="pay-essieu-btn">
                Payer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Calendar of upcoming deadlines */}
      <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Calendrier fiscal
        </h3>
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
          {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
            const mDeadlines = getTaxDeadlines(m, year, taxRegime);
            const isCurrent = m === month;
            const isPast = m < month;
            return (
              <div
                key={m}
                className={`rounded-lg p-2 text-center border transition-colors ${
                  isCurrent ? 'border-accent bg-accent/10' :
                  isPast ? 'border-ink-600 bg-ink-700/30 opacity-60' :
                  mDeadlines.length > 0 ? 'border-warn/40 bg-warn/10' : 'border-ink-600'
                }`}
              >
                <p className={`text-xs font-bold ${isCurrent ? 'text-foreground' : 'text-slate-300'}`}>M{m}</p>
                {mDeadlines.map((d) => (
                  <p key={d.type} className="text-[8px] text-slate-500 mt-0.5 truncate">{d.type.toUpperCase()}</p>
                ))}
                {mDeadlines.length === 0 && <p className="text-[8px] text-slate-600 mt-0.5">—</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Tax alerts */}
      {taxAlerts.length > 0 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-warn" /> Alertes fiscales
          </h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {taxAlerts.slice(-10).reverse().map(a => (
              <div key={a.id} className={`flex items-start gap-2 text-xs p-2 rounded-lg ${
                a.severity === 'grave' ? 'bg-danger/10 text-danger' : 'bg-warn/10 text-warn'
              }`}>
                {a.type === 'audit' ? <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> : <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />}
                <span>{a.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
